import {
  formatarPlaca,
  ROTULO_DO_STATUS_DE_VEICULO,
  ROTULO_DO_TIPO_DE_LOCAL,
  salvarPlanoDeManutencaoSchema,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRightLeft,
  CircuitBoard,
  Eye,
  Gauge,
  History,
  LoaderCircle,
  type LucideIcon,
  Pencil,
  Pentagon,
  Radius,
  RefreshCw,
  Route,
  Satellite,
  ShieldAlert,
  ShieldCheck,
  Wrench,
} from 'lucide-react'
import { type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Paginacao } from '@/components/data/paginacao'
import { Mapa, type PinoDoMapa, ZOOM_COM_ENDERECO, ZOOM_SEM_ENDERECO } from '@/components/mapa/mapa'
import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { NotFoundState } from '@/components/state/not-found-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  diasAte,
  gravidadeDaData,
  ROTULO_DA_ORIGEM_DA_PREMISSA,
  TAMANHO_DO_HISTORICO,
  useHistoricoDeStatus,
  useSalvarPlanoDeManutencao,
  useVeiculo,
  type PlanoDeManutencao,
  type PremissasDeDepreciacao,
  type Veiculo,
  type VeiculoDetalhado,
} from '@/features/frota/api'
import {
  BadgeDeGravidadeDeAlerta,
  BadgeDeNivelDeBlindagem,
  BadgeDeStatusDoVeiculo,
} from '@/features/frota/badges'
import {
  defasagemDaLeitura,
  horaDe,
  leituraEstaDefasada,
  porDia,
  resumoDasOcorrencias,
  ROTULO_DA_OCORRENCIA,
  TIPOS_DE_OCORRENCIA,
  type TipoDeOcorrencia,
} from '@/features/frota/rastreamento'
import { DialogoDeStatus } from '@/features/frota/dialogo-de-status'
import { DialogoDeVeiculo } from '@/features/frota/dialogo-de-veiculo'
import { ApiError, apiGet, apiPost, errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora, SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { ROTAS_DE_RASTREAMENTO } from '@/lib/endpoints'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { usePermissao } from '@/lib/permissoes'
import { montarQuery } from '@/lib/query-string'

/**
 * O detalhe de um carro.
 *
 * ---------------------------------------------------------------------------
 * A TRILHA DE STATUS É O CONTEÚDO PRINCIPAL
 * ---------------------------------------------------------------------------
 * O cadastro qualquer planilha guarda. O que só existe aqui é a sequência de
 * mudanças com autor, horário e MOTIVO — é ela que responde "por que este carro
 * ficou nove dias na funilaria em março" quando, dois anos depois, alguém decide
 * se renova ou vende o ativo. Ela é append-only: nada é corrigido, nada é
 * apagado, e a tela não oferece nenhum caminho que sugira o contrário.
 *
 * ---------------------------------------------------------------------------
 * AS PREMISSAS CHEGAM RESOLVIDAS, E A ORIGEM VEM JUNTO
 * ---------------------------------------------------------------------------
 * Vida útil e residual passam pela cascata veículo → categoria → política. Quem
 * resolve é o motor determinístico do `@locaveri/shared`, no servidor; o painel
 * só exibe. Mostrar "60 meses" sem dizer que vieram da política faria alguém
 * criar um override achando que estava confirmando o número — por isso cada
 * premissa aparece com a etiqueta de onde veio.
 *
 * ---------------------------------------------------------------------------
 * O ID VEM DE `strict: false`, NÃO DE UM `from`
 * ---------------------------------------------------------------------------
 * Mesmo motivo documentado em `lib/rota.ts`: as rotas do painel são declaradas
 * em código, e `useParams({ from: '/frota/$veiculoId' })` obrigaria esta tela a
 * repetir o identificador interno da rota — uma string que muda quando a árvore
 * é reorganizada e que o TypeScript não confere contra o arquivo da tela. Com
 * `strict: false` o contrato da tela é o parâmetro que ela lê, e o roteador
 * continua responsável por decodificar o valor.
 */

/** Quilometragem e percentual — números que não são dinheiro, mas alinham igual. */
const NUMERO_PT_BR = new Intl.NumberFormat('pt-BR')

function formatarKm(valor: number): string {
  return `${NUMERO_PT_BR.format(valor)} km`
}

/** Pontos-base → percentual: 2000 vira `20%`. */
function formatarPontosBase(valor: number): string {
  return `${NUMERO_PT_BR.format(valor / 100)}%`
}

export function VeiculoPage(): ReactElement {
  const podeEditar = usePermissao('vehicle.update')
  const podeEditarPlano = usePermissao('maintenance_plan.update')

  // `?? null` porque a rota pode não ter o parâmetro (endereço digitado à mão):
  // aí a tela é "não encontrada", não uma consulta com id vazio.
  const veiculoId = useParams({ strict: false }).veiculoId ?? null

  const consulta = useVeiculo(veiculoId)

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [veiculoDoStatus, setVeiculoDoStatus] = useState<Veiculo | null>(null)

  if (veiculoId === null) {
    return <NotFoundState />
  }

  if (consulta.isPending) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <LoadingState rotulo="Carregando o veículo…" />
      </div>
    )
  }

  if (consulta.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <ErrorState
          titulo="Não foi possível carregar o veículo"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      </div>
    )
  }

  const veiculo = consulta.data

  // Carro baixado é arquivo, não frota: a API recusa editar, mudar status e
  // salvar plano enquanto a baixa não for revertida (`VEICULO_BAIXADO`, 409).
  // Oferecer os botões aqui seria oferecer três caminhos que só terminam em
  // erro.
  const naFrota = veiculo.dataDeBaixa === null

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Link
          to="/frota"
          className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-secondary
            hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Frota
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">
              {veiculo.modelo}{' '}
              <span className="tabular-money text-fg-secondary">
                {formatarPlaca(veiculo.placa)}
              </span>
            </h1>
            <p className="mt-1 text-sm text-fg-secondary">
              {veiculo.marca} · {veiculo.anoFabricacao}/{veiculo.anoModelo} · {veiculo.cor} ·{' '}
              {veiculo.categoria.nome}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <BadgeDeStatusDoVeiculo status={veiculo.statusAtual} />
              <BadgeDeNivelDeBlindagem nivel={veiculo.categoria.nivelDeBlindagem} />
              {veiculo.dataDeBaixa === null ? null : (
                <Badge variant="neutral">Baixado em {formatarData(veiculo.dataDeBaixa)}</Badge>
              )}
            </div>
          </div>

          {podeEditar && naFrota ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setDialogoAberto(true)
                }}
              >
                <Pencil aria-hidden="true" />
                Editar cadastro
              </Button>

              {/* O único botão de marca da tela: mudar status é a ação que a
                  operação repete todo dia. Todo o resto aqui é secundário —
                  amarelo é acento, e um segundo amarelo tiraria o peso deste. */}
              <Button
                onClick={() => {
                  setVeiculoDoStatus(veiculo)
                }}
              >
                <ArrowRightLeft aria-hidden="true" />
                Mudar status
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {naFrota ? null : (
        <Alert variant="neutral">
          <AlertDescription>
            Veículo baixado da frota em {formatarData(veiculo.dataDeBaixa)}. O cadastro, os custos e
            a trilha continuam consultáveis; editar, mudar status e alterar o plano de revisão só
            voltam se a baixa for revertida.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <CartaoDeDados veiculo={veiculo} />
        <CartaoDeVencimentos veiculo={veiculo} />
        <CartaoDePremissas premissas={veiculo.premissasDeDepreciacao} />
        <CartaoDoPlano
          veiculoId={veiculo.id}
          plano={veiculo.planoDeManutencao}
          hodometroAtual={veiculo.hodometroAtual}
          // Baixado é só leitura: `salvarPlanoDeManutencao` responde 409
          // `VEICULO_BAIXADO`, então o formulário só levaria a um erro.
          podeEditar={podeEditarPlano && naFrota}
        />
        <CartaoDeRastreamento veiculoId={veiculo.id} placa={veiculo.placa} />
        <CartaoDeOcorrencias veiculoId={veiculo.id} />
      </div>

      <TrilhaDeStatus veiculoId={veiculo.id} />

      <DialogoDeVeiculo
        aberto={dialogoAberto}
        veiculo={veiculo}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDeStatus
        veiculo={veiculoDoStatus}
        aoFechar={() => {
          setVeiculoDoStatus(null)
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Blocos
// ---------------------------------------------------------------------------

interface DadoProps {
  readonly rotulo: string
  readonly children: ReactNode
  readonly className?: string
}

function Dado({ rotulo, children, className }: DadoProps): ReactElement {
  return (
    <div className={className}>
      <dt className="text-xs text-fg-muted">{rotulo}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  )
}

function CartaoDeDados({ veiculo }: { readonly veiculo: VeiculoDetalhado }): ReactElement {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Dados do veículo</CardTitle>
      </CardHeader>

      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">
          <Dado rotulo="Placa">
            <span className="tabular-money">{formatarPlaca(veiculo.placa)}</span>
          </Dado>
          <Dado rotulo="Cor">{veiculo.cor}</Dado>
          <Dado rotulo="RENAVAM">
            <span className="tabular-money">{veiculo.renavam}</span>
          </Dado>
          <Dado rotulo="Chassi">
            <span className="tabular-money break-all">{veiculo.chassi}</span>
          </Dado>
          <Dado rotulo="Categoria">{veiculo.categoria.nome}</Dado>
          <Dado rotulo="Base">
            {veiculo.local === null
              ? SEM_VALOR
              : `${veiculo.local.nome} · ${ROTULO_DO_TIPO_DE_LOCAL[veiculo.local.tipo]}`}
          </Dado>
          <Dado rotulo="Hodômetro">
            <span className="tabular-money">{formatarKm(veiculo.hodometroAtual)}</span>
          </Dado>
          <Dado rotulo="Prioridade de alocação">
            <span className="tabular-money">{veiculo.prioridadeDeAlocacao}</span>
          </Dado>
          <Dado rotulo="Data de aquisição">
            <span className="tabular-money">{formatarData(veiculo.dataDeAquisicao)}</span>
          </Dado>
          <Dado rotulo="Valor de aquisição">
            <span className="tabular-money">{formatarMoedaDoWire(veiculo.valorDeAquisicao)}</span>
          </Dado>
          <Dado rotulo="Valor atual estimado">
            <span className="tabular-money">{formatarMoedaDoWire(veiculo.valorAtualEstimado)}</span>
          </Dado>
          <Dado rotulo="Certificado de blindagem">
            {veiculo.certificadoDeBlindagemNumero ?? SEM_VALOR}
          </Dado>

          {veiculo.laudoDeBlindagemUrl === null ? null : (
            <Dado rotulo="Laudo de blindagem" className="sm:col-span-2">
              <a
                href={veiculo.laudoDeBlindagemUrl}
                target="_blank"
                rel="noreferrer"
                className="text-brand underline underline-offset-4"
              >
                Abrir o laudo
              </a>
            </Dado>
          )}

          {veiculo.notasInternas === null ? null : (
            <Dado rotulo="Notas internas" className="sm:col-span-2">
              <span className="whitespace-pre-line text-fg-secondary">{veiculo.notasInternas}</span>
            </Dado>
          )}
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * As premissas que o fechamento mensal usa. Números de leitura, nunca de
 * edição: quem muda a herança é o formulário do veículo (override) ou a política
 * de custo — e é justamente por isso que a origem aparece ao lado de cada um.
 */
function CartaoDePremissas({
  premissas,
}: {
  readonly premissas: PremissasDeDepreciacao
}): ReactElement {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Depreciação</CardTitle>
        <p className="text-sm text-fg-secondary">
          Resolvidas pela cascata veículo → categoria → política de custo.
        </p>
      </CardHeader>

      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">
          <Dado rotulo="Base de cálculo">
            <span className="tabular-money">{formatarMoedaDoWire(premissas.valorDeAquisicao)}</span>
          </Dado>

          <Dado rotulo="Vida útil">
            <span className="tabular-money">{premissas.vidaUtilEmMeses} meses</span>
            <Badge variant="outline" className="ml-2">
              {ROTULO_DA_ORIGEM_DA_PREMISSA[premissas.origemDaVidaUtil]}
            </Badge>
          </Dado>

          <Dado rotulo="Valor residual" className="sm:col-span-2">
            <span className="tabular-money">
              {formatarPontosBase(premissas.residualEmPontosBase)}
            </span>
            <Badge variant="outline" className="ml-2">
              {ROTULO_DA_ORIGEM_DA_PREMISSA[premissas.origemDoResidual]}
            </Badge>
          </Dado>
        </dl>

        <p className="mt-4 text-xs text-fg-muted">
          Deixar o campo em branco no cadastro do veículo mantém a herança; preencher cria uma
          exceção só para este carro.
        </p>
      </CardContent>
    </Card>
  )
}

interface VencimentoProps {
  readonly rotulo: string
  readonly data: string | null
  readonly detalhe?: string | undefined
}

/**
 * Uma linha de vencimento. A gravidade sai do MESMO corte do servidor
 * (`gravidadeDoVencimento`, via `gravidadeDaData`), senão a tela e o painel de
 * alertas discordariam sobre o que é crítico.
 */
function LinhaDeVencimento({ rotulo, data, detalhe }: VencimentoProps): ReactElement {
  const gravidade = gravidadeDaData(data)
  const dias = diasAte(data)

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="text-sm text-foreground">{rotulo}</p>
        {detalhe === undefined ? null : <p className="text-xs text-fg-muted">{detalhe}</p>}
      </div>

      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap tabular-money text-sm text-fg-secondary">
          {data === null ? SEM_VALOR : formatarData(data)}
        </span>
        {gravidade === null ? (
          dias === null ? null : (
            <Badge variant="neutral">Em dia</Badge>
          )
        ) : (
          <BadgeDeGravidadeDeAlerta gravidade={gravidade} />
        )}
      </div>
    </li>
  )
}

/** `faltam 3.500 km` / `1.200 km além`. `null` quando não há gatilho por km. */
function prazoEmKm(proximaRevisaoKm: number | null, hodometroAtual: number): string | null {
  if (proximaRevisaoKm === null) {
    return null
  }

  const restante = proximaRevisaoKm - hodometroAtual

  return restante < 0 ? `${formatarKm(Math.abs(restante))} além` : `faltam ${formatarKm(restante)}`
}

function CartaoDeVencimentos({ veiculo }: { readonly veiculo: VeiculoDetalhado }): ReactElement {
  const plano = veiculo.planoDeManutencao
  const prazoDaRevisao = prazoEmKm(plano?.proximaRevisaoKm ?? null, veiculo.hodometroAtual)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Vencimentos</CardTitle>
        <p className="text-sm text-fg-secondary">
          Nesta fase o vencimento é alerta; o bloqueio de alocação entra com a reserva.
        </p>
      </CardHeader>

      <CardContent>
        <ul className="divide-y divide-border">
          <LinhaDeVencimento
            rotulo="Certificado de blindagem"
            data={veiculo.certificadoDeBlindagemValidade}
            detalhe={veiculo.certificadoDeBlindagemNumero ?? 'sem número registrado'}
          />
          <LinhaDeVencimento
            rotulo="Apólice de seguro"
            data={veiculo.apoliceVencimento}
            detalhe={
              veiculo.seguradora === null
                ? 'sem seguradora registrada'
                : `${veiculo.seguradora}${veiculo.apolice === null ? '' : ` · ${veiculo.apolice}`}`
            }
          />
          <LinhaDeVencimento rotulo="IPVA" data={veiculo.ipvaVencimento} />
          <LinhaDeVencimento rotulo="Licenciamento" data={veiculo.licenciamentoVencimento} />
          <LinhaDeVencimento
            rotulo="Revisão (data)"
            data={plano?.proximaRevisaoData ?? null}
            detalhe={plano === null ? 'sem plano de revisão' : undefined}
          />
        </ul>

        {/* Quilometragem não vira data: converter uma na outra exigiria supor
            uma média de rodagem que o sistema não tem. */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <div>
            <p className="text-sm text-foreground">Revisão (quilometragem)</p>
            <p className="text-xs text-fg-muted">
              Hodômetro em{' '}
              <span className="tabular-money">{formatarKm(veiculo.hodometroAtual)}</span>
            </p>
          </div>
          <span className="whitespace-nowrap tabular-money text-sm text-fg-secondary">
            {prazoDaRevisao ?? SEM_VALOR}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Plano de revisão
// ---------------------------------------------------------------------------

const CAMPOS_DO_PLANO = [
  'proximaRevisaoKm',
  'proximaRevisaoData',
  'alertaKmAntes',
  'alertaDiasAntes',
  'observacoes',
  'ativo',
] as const

interface FormularioDoPlano {
  proximaRevisaoKm: string
  proximaRevisaoData: string
  alertaKmAntes: string
  alertaDiasAntes: string
  observacoes: string
  ativo: boolean
}

function valoresDoPlano(plano: PlanoDeManutencao | null): FormularioDoPlano {
  return {
    proximaRevisaoKm: String(plano?.proximaRevisaoKm ?? ''),
    proximaRevisaoData: (plano?.proximaRevisaoData ?? '').slice(0, 10),
    alertaKmAntes: String(plano?.alertaKmAntes ?? 1000),
    alertaDiasAntes: String(plano?.alertaDiasAntes ?? 30),
    observacoes: plano?.observacoes ?? '',
    ativo: plano?.ativo ?? true,
  }
}

interface PlanoProps {
  readonly veiculoId: string
  readonly plano: PlanoDeManutencao | null
  readonly hodometroAtual: number
  readonly podeEditar: boolean
}

/**
 * Plano de revisão — os DOIS gatilhos convivem e o que vencer primeiro manda.
 *
 * Blindado roda pouco e envelhece igual: com gatilho só por quilometragem, a
 * revisão de um carro que fica parado passaria anos sem acontecer. Por isso o
 * contrato exige ao menos um dos dois, e a tela deixa isso escrito antes de o
 * erro aparecer.
 */
function CartaoDoPlano({ veiculoId, plano, hodometroAtual, podeEditar }: PlanoProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const salvar = useSalvarPlanoDeManutencao()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormularioDoPlano>({ defaultValues: valoresDoPlano(plano) })

  // O plano chega do servidor depois do primeiro render (e de novo a cada
  // salvamento, porque a mutação invalida o detalhe): sem este efeito, o
  // formulário continuaria mostrando o estado anterior.
  useEffect(() => {
    reset(valoresDoPlano(plano))
  }, [plano, reset])

  const ocupado = salvar.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const entrada = {
      proximaRevisaoKm:
        valores.proximaRevisaoKm.trim().length === 0 ? null : valores.proximaRevisaoKm,
      proximaRevisaoData:
        valores.proximaRevisaoData.trim().length === 0 ? null : valores.proximaRevisaoData,
      ...(valores.alertaKmAntes.trim().length === 0
        ? {}
        : { alertaKmAntes: valores.alertaKmAntes }),
      ...(valores.alertaDiasAntes.trim().length === 0
        ? {}
        : { alertaDiasAntes: valores.alertaDiasAntes }),
      observacoes: valores.observacoes.trim().length === 0 ? null : valores.observacoes,
      ativo: valores.ativo,
    }

    const validado = salvarPlanoDeManutencaoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS_DO_PLANO))
      return
    }

    // O contrato do plano não transforma dinheiro nem `bigint`: aqui
    // `validado.data` é o valor já normalizado e serializável.
    salvar.mutate(
      { veiculoId, dados: validado.data },
      {
        onSuccess: () => {
          toast.success('Plano de revisão salvo')
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS_DO_PLANO))
        },
      },
    )
  })

  if (!podeEditar) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Plano de revisão</CardTitle>
        </CardHeader>
        <CardContent>
          {plano === null ? (
            <EmptyState
              icone={Wrench}
              titulo="Sem plano de revisão"
              descricao="Nenhum gatilho de revisão foi definido para este veículo."
            />
          ) : (
            <dl className="grid gap-4 sm:grid-cols-2">
              <Dado rotulo="Próxima revisão (km)">
                <span className="tabular-money">
                  {plano.proximaRevisaoKm === null ? SEM_VALOR : formatarKm(plano.proximaRevisaoKm)}
                </span>
              </Dado>
              <Dado rotulo="Próxima revisão (data)">
                <span className="tabular-money">{formatarData(plano.proximaRevisaoData)}</span>
              </Dado>
              <Dado rotulo="Aviso antecipado">
                <span className="tabular-money">
                  {formatarKm(plano.alertaKmAntes)} · {plano.alertaDiasAntes} dias
                </span>
              </Dado>
              <Dado rotulo="Situação">{plano.ativo ? 'Ativo' : 'Suspenso'}</Dado>
            </dl>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Plano de revisão</CardTitle>
        <p className="text-sm text-fg-secondary">
          Informe ao menos um gatilho. Vale o que vencer primeiro — quilometragem ou data.
        </p>
      </CardHeader>

      <CardContent>
        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Próxima revisão (km)"
              erro={errors.proximaRevisaoKm?.message}
              dica={`Hodômetro hoje: ${formatarKm(hodometroAtual)}.`}
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('proximaRevisaoKm')}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Próxima revisão (data)" erro={errors.proximaRevisaoData?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('proximaRevisaoData')}
                  type="date"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Avisar (km antes)"
              erro={errors.alertaKmAntes?.message}
              dica="Quantos quilômetros antes o alerta aparece."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('alertaKmAntes')}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Avisar (dias antes)"
              erro={errors.alertaDiasAntes?.message}
              dica="Quantos dias antes o alerta aparece."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('alertaDiasAntes')}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          <Campo rotulo="Observações" erro={errors.observacoes?.message}>
            {(campo) => (
              <Textarea
                {...campo}
                {...register('observacoes')}
                rows={2}
                maxLength={1000}
                disabled={ocupado}
              />
            )}
          </Campo>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-fg-secondary">
            <Checkbox {...register('ativo')} className="mt-0.5" disabled={ocupado} />
            <span>Plano ativo — desligue para parar os alertas sem apagar os gatilhos</span>
          </label>

          {/* `secondary`, e não o amarelo: o CTA de marca desta tela é "Mudar
              status", no cabeçalho. Dois amarelos competindo é o mesmo que
              nenhum. */}
          <Button type="submit" variant="secondary" disabled={ocupado}>
            {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Salvar plano
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Rastreamento (Gap 5)
// ---------------------------------------------------------------------------

/**
 * Contratos locais, espelhando `rastreamento.dto.ts` da API — o padrão de
 * `features/vistorias/api.ts` para DTO que só o painel lê. Vivem NESTA página
 * (e não em `features/`) porque o card é o único consumidor; nada é exportado,
 * que é o que o `react-refresh` exige em `src/pages/**`.
 *
 * Latitude e longitude ENTRARAM em 16/09/2026, com o mapa. Antes ficavam de fora
 * com o argumento de que "o card não tem mapa". O corte que vale é o da API: o
 * PINO sai em toda leitura de posição — e toda leitura já vai para a trilha
 * como `TELEMETRIA_CONSULTADA` —; a RUA (o endereço geocodificado) só com o
 * clique em "Revelar endereço", que a trilha registra à parte. A coordenada
 * crua NUNCA aparece como texto na tela: é o mapa que a mostra.
 */
const MOTIVOS_DE_POSICAO_AUSENTE = ['SEM_RASTREADOR', 'SEM_POSICAO'] as const

const posicaoDoRastreadorSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  velocidadeKmH: z.number().int().nullable(),
  ignicaoLigada: z.boolean().nullable(),
  /** `BIGINT` como string — a regra de transporte do dinheiro, pelo mesmo motivo. */
  odometroEmMetros: z.string().nullable(),
  /** `null` = mascarado (o padrão) OU sem geocodificação; `enderecoRevelado` desambigua. */
  endereco: z.string().nullable(),
  /** Quando o EQUIPAMENTO registrou (relógio do dispositivo). */
  registradoEm: z.string(),
  recebidoEm: z.string(),
})

const posicaoDaApiSchema = z.object({
  /** `null` quando não há o que mostrar — e `motivo` diz por quê. */
  posicao: posicaoDoRastreadorSchema.nullable(),
  motivo: z.enum(MOTIVOS_DE_POSICAO_AUSENTE).nullable(),
  /** Espelha o que foi pedido E auditado. */
  enderecoRevelado: z.boolean(),
})

const resultadoDaSincronizacaoSchema = z.object({
  vinculados: z.number().int().nonnegative(),
  divergencias: z.object({
    /** Placas que a 3S monitora e a Locaveri não cadastrou. */
    soNa3s: z.array(z.string()),
    /** Placas da frota Locaveri que a 3S não monitora. */
    soNaLocaveri: z.array(z.string()),
  }),
})

type ResultadoDaSincronizacao = z.infer<typeof resultadoDaSincronizacaoSchema>

const CHAVE_DE_POSICAO = 'rastreamento-posicao'

/**
 * A última posição — do NOSSO banco, nunca da 3S ao vivo. Cada consulta entra
 * na trilha de auditoria do servidor (`TELEMETRIA_CONSULTADA`), então nada de
 * refetch automático: `staleTime` alto, sem refetch no foco e sem retry —
 * repetir uma leitura auditada sem gesto humano é gerar trilha sem gente.
 */
function usePosicaoDoRastreador(
  veiculoId: string,
  revelarEndereco: boolean,
  habilitado: boolean,
): UseQueryResult<z.infer<typeof posicaoDaApiSchema>> {
  return useQuery({
    queryKey: [CHAVE_DE_POSICAO, veiculoId, revelarEndereco],
    queryFn: ({ signal }) =>
      apiGet(
        `${ROTAS_DE_RASTREAMENTO.posicao(veiculoId)}${montarQuery({
          // `montarQuery` descarta `null`: o padrão mascarado viaja como AUSÊNCIA.
          revelarEndereco: revelarEndereco ? true : null,
        })}`,
        posicaoDaApiSchema,
        { signal },
      ),
    enabled: habilitado,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/** Reconciliação POR PLACA com a frota da 3S — nunca cria veículo. */
function useSincronizarFrota(): UseMutationResult<ResultadoDaSincronizacao, Error, void> {
  return useMutation({
    mutationFn: () =>
      apiPost(ROTAS_DE_RASTREAMENTO.sincronizarFrota, resultadoDaSincronizacaoSchema, undefined),
  })
}

/** Metros (BIGINT em string) → km formatado. `null` quando a string não é número. */
function kmDoRastreador(odometroEmMetros: string): string | null {
  try {
    return formatarKm(Number(BigInt(odometroEmMetros) / 1000n))
  } catch {
    return null
  }
}

/**
 * A última posição do rastreador 3S.
 *
 * O card NÃO aparece para quem não tem `telemetry.read` — é a permissão que
 * nenhum papel recebe por padrão além do GESTOR, porque posição de cliente é o
 * dado mais sensível do sistema. O 403 da API (permissão revogada depois do
 * token emitido) some com o card em vez de virar erro: oferecer a tela e negar
 * o conteúdo só ensinaria onde o dado mora.
 *
 * O endereço vem MASCARADO por padrão; revelar refaz a consulta com
 * `revelarEndereco=true`, que o servidor audita nominalmente (Adendo E/LGPD) —
 * e a tela avisa isso ANTES do clique, não depois.
 */
function CartaoDeRastreamento({
  veiculoId,
  placa,
}: {
  readonly veiculoId: string
  readonly placa: string
}): ReactElement | null {
  const podeLer = usePermissao('telemetry.read')
  const podeSincronizar = usePermissao('telemetry.update')
  const queryClient = useQueryClient()

  const [revelarEndereco, setRevelarEndereco] = useState(false)
  const [sincronizacao, setSincronizacao] = useState<ResultadoDaSincronizacao | null>(null)

  const consulta = usePosicaoDoRastreador(veiculoId, revelarEndereco, podeLer)
  const sincronizar = useSincronizarFrota()

  if (!podeLer) {
    return null
  }

  if (consulta.isError && ApiError.is(consulta.error) && consulta.error.status === 403) {
    return null
  }

  function aoSincronizar(): void {
    sincronizar.mutate(undefined, {
      onSuccess: (resultado) => {
        setSincronizacao(resultado)
        // A posição pode ter aparecido junto com o vínculo novo.
        void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_POSICAO, veiculoId] })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível sincronizar a frota', { description: errorMessage(causa) })
      },
    })
  }

  const dados = consulta.data
  const posicao = dados?.posicao ?? null

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Rastreamento</CardTitle>
        <p className="text-sm text-fg-secondary">
          A última posição espelhada do rastreador 3S. Toda consulta fica registrada na trilha de
          auditoria.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {consulta.isPending ? (
          <LoadingState rotulo="Consultando a posição…" linhas={3} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível consultar a posição"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : posicao === null ? (
          dados?.motivo === 'SEM_RASTREADOR' ? (
            <EmptyState
              icone={Satellite}
              titulo="Sem rastreador vinculado"
              descricao="O vínculo é feito por placa, reconciliando a frota com a 3S. Se o rastreador já foi instalado, sincronize — a placa que não casar volta nomeada no relatório de divergências."
              acao={
                podeSincronizar ? (
                  <Button
                    variant="secondary"
                    disabled={sincronizar.isPending}
                    onClick={aoSincronizar}
                  >
                    {sincronizar.isPending ? (
                      <LoaderCircle className="animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw aria-hidden="true" />
                    )}
                    Sincronizar frota
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <EmptyState
              icone={Satellite}
              titulo="Rastreador ainda sem posição"
              descricao="O veículo está vinculado, mas nenhuma leitura chegou — normal logo depois da instalação. A primeira posição entra no próximo ciclo de sincronização."
            />
          )
        ) : (
          <>
            {leituraEstaDefasada(posicao.registradoEm) ? (
              <Alert variant="warning">
                <AlertTitle>Leitura defasada</AlertTitle>
                <AlertDescription>
                  A última posição foi registrada{' '}
                  {defasagemDaLeitura(posicao.registradoEm) ?? 'há mais de 2 h'} e o rastreador não
                  reporta desde então. Isso é esperado em veículo desligado há tempo; não sendo o
                  caso, é sinal de equipamento sem sinal ou sem alimentação.
                </AlertDescription>
              </Alert>
            ) : null}

            <Mapa
              className="h-56 sm:h-72"
              // A RUA só depois do clique auditado. Ver `ZOOM_SEM_ENDERECO`.
              zoomMaximo={revelarEndereco ? ZOOM_COM_ENDERECO : ZOOM_SEM_ENDERECO}
              rotuloAcessivel={`Mapa com a última posição do veículo ${formatarPlaca(placa)}`}
              pinos={[
                {
                  id: veiculoId,
                  latitude: posicao.latitude,
                  longitude: posicao.longitude,
                  rotulo: formatarPlaca(placa),
                  titulo: formatarPlaca(placa),
                  linhas: [
                    `Registrada ${defasagemDaLeitura(posicao.registradoEm) ?? 'sem data'}`,
                    posicao.velocidadeKmH === null
                      ? 'Velocidade não informada'
                      : `${String(posicao.velocidadeKmH)} km/h`,
                  ],
                  // Leitura velha não ganha o amarelo de "está aqui agora".
                  tom: leituraEstaDefasada(posicao.registradoEm) ? 'alerta' : 'marca',
                },
              ]}
            />

            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Dado rotulo="Registrada em">
                <span className="tabular-money">{formatarDataHora(posicao.registradoEm)}</span>
                {defasagemDaLeitura(posicao.registradoEm) === null ? null : (
                  <span className="ml-1.5 text-fg-muted">
                    ({defasagemDaLeitura(posicao.registradoEm)})
                  </span>
                )}
              </Dado>
              <Dado rotulo="Velocidade">
                {posicao.velocidadeKmH === null ? (
                  SEM_VALOR
                ) : (
                  <span className="tabular-money">{posicao.velocidadeKmH} km/h</span>
                )}
              </Dado>
              {/* Nunca só cor: ignição é texto, como todo status do painel. */}
              <Dado rotulo="Ignição">
                {posicao.ignicaoLigada === null
                  ? SEM_VALOR
                  : posicao.ignicaoLigada
                    ? 'Ligada'
                    : 'Desligada'}
              </Dado>
              <Dado rotulo="Km do rastreador">
                {posicao.odometroEmMetros === null ? (
                  SEM_VALOR
                ) : (
                  <span className="tabular-money">
                    {kmDoRastreador(posicao.odometroEmMetros) ?? SEM_VALOR}
                  </span>
                )}
              </Dado>

              <Dado rotulo="Endereço" className="sm:col-span-2 lg:col-span-4">
                {/* `posicao !== null` garante `dados`, mas o narrowing não
                    atravessa o `?? null` da derivação — daí o opcional. */}
                {dados?.enderecoRevelado === true ? (
                  <>
                    {posicao.endereco ?? 'Sem geocodificação para esta posição.'}
                    <span className="mt-0.5 block text-xs text-fg-muted">
                      Endereço revelado — a consulta ficou na trilha de auditoria.
                    </span>
                  </>
                ) : (
                  <div className="space-y-1.5">
                    <span className="block text-fg-secondary">
                      Mascarado por padrão — o mapa mostra a cidade e a região, não a rua.
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={consulta.isFetching}
                      onClick={() => {
                        setRevelarEndereco(true)
                      }}
                    >
                      <Eye aria-hidden="true" />
                      Revelar endereço
                    </Button>
                    <span className="block text-xs text-fg-muted">
                      Endereço é a localização do cliente com o carro: revelar fica registrado na
                      trilha de auditoria, com o seu usuário (LGPD).
                    </span>
                  </div>
                )}
              </Dado>
            </dl>
          </>
        )}

        {sincronizacao === null ? null : (
          <Alert variant="info">
            <AlertTitle>Sincronização concluída</AlertTitle>
            <AlertDescription>
              <span className="tabular-money">{sincronizacao.vinculados}</span>{' '}
              {sincronizacao.vinculados === 1 ? 'veículo vinculado' : 'veículos vinculados'}.
              {sincronizacao.divergencias.soNa3s.length === 0 &&
              sincronizacao.divergencias.soNaLocaveri.length === 0 ? (
                <span className="block">Nenhuma divergência de placa.</span>
              ) : (
                <>
                  {sincronizacao.divergencias.soNa3s.length === 0 ? null : (
                    <span className="block">
                      Só na 3S (rastreadas sem cadastro aqui):{' '}
                      <span className="tabular-money">
                        {sincronizacao.divergencias.soNa3s.map(formatarPlaca).join(', ')}
                      </span>
                    </span>
                  )}
                  {sincronizacao.divergencias.soNaLocaveri.length === 0 ? null : (
                    <span className="block">
                      Só na Locaveri (frota sem rastreador):{' '}
                      <span className="tabular-money">
                        {sincronizacao.divergencias.soNaLocaveri.map(formatarPlaca).join(', ')}
                      </span>
                    </span>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Cerca virtual e alertas (Gap 5)
// ---------------------------------------------------------------------------

/**
 * A leitura das ocorrências — e por que ela NÃO consulta sozinha.
 *
 * Três razões, e as três valem mais que a conveniência de abrir a tela pronta:
 *
 *   1. é a ÚNICA rota do módulo que fala com a 3S dentro do request, e a cota
 *      é de 9 chamadas por minuto para a instalação inteira — abrir a página de
 *      dez carros em sequência esgotaria o minuto;
 *   2. cada consulta vira `TELEMETRIA_CONSULTADA` na trilha, com nome e IP.
 *      Trilha gerada por montagem de componente é trilha sem gente;
 *   3. o período é escolha de quem lê, e não existe padrão honesto — "últimos
 *      7 dias" responde outra pergunta que "últimos 90".
 *
 * É o mesmo raciocínio do `revelarEndereco` do cartão de posição: o gesto vem
 * primeiro, e a tela diz o que ele custa ANTES do clique.
 */
const ICONE_DA_OCORRENCIA: Record<TipoDeOcorrencia, LucideIcon> = {
  ALERTA_VELOCIDADE: Gauge,
  ALERTA_SENSOR: CircuitBoard,
  CERCA_ALVO: Radius,
  CERCA_LOGRADOURO: Route,
  CERCA_POLIGONAL: Pentagon,
}

const ocorrenciaDaApiSchema = z.object({
  tipo: z.enum(TIPOS_DE_OCORRENCIA),
  /** Nível de risco da CERCA, cadastrado na 3S. `null` nos alertas. */
  nivelRisco: z.number().int().nullable(),
  /** `null` quando a 3S não mandou data legível — o item vale mesmo assim. */
  quando: z.string().nullable(),
  descricao: z.string(),
  /** Onde o CARRO estava quando disparou. Pino no mapa; a rua nunca vem aqui. */
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
})

const ocorrenciasDaApiSchema = z.object({
  /** `SEM_RASTREADOR` quando o veículo não tem vínculo — com `itens` vazio. */
  motivo: z.literal('SEM_RASTREADOR').nullable(),
  itens: z.array(ocorrenciaDaApiSchema),
})

/** As janelas oferecidas. O teto do servidor é 92 dias; 90 cabe com folga. */
const PERIODOS_DE_OCORRENCIA = [
  // `frase` existe separada de `rotulo` porque minúscula não é tradução: o
  // resumo dizia "3 ocorrências últimos 30 dias", que não é português.
  { dias: 7, rotulo: 'Últimos 7 dias', frase: 'nos últimos 7 dias' },
  { dias: 30, rotulo: 'Últimos 30 dias', frase: 'nos últimos 30 dias' },
  { dias: 90, rotulo: 'Últimos 90 dias', frase: 'nos últimos 90 dias' },
] as const

const CHAVE_DE_OCORRENCIAS = 'rastreamento-ocorrencias'

/**
 * `enabled: false` de propósito: quem dispara é o botão, com `refetch`. Ver o
 * bloco acima — as três razões pelas quais esta consulta não roda sozinha.
 */
function useOcorrencias(
  veiculoId: string,
  dias: number,
): UseQueryResult<z.infer<typeof ocorrenciasDaApiSchema>> {
  return useQuery({
    queryKey: [CHAVE_DE_OCORRENCIAS, veiculoId, dias],
    queryFn: ({ signal }) => {
      const ate = new Date()
      const de = new Date(ate.getTime() - dias * 86_400_000)
      return apiGet(
        `${ROTAS_DE_RASTREAMENTO.ocorrencias(veiculoId)}${montarQuery({
          de: de.toISOString(),
          ate: ate.toISOString(),
        })}`,
        ocorrenciasDaApiSchema,
        { signal },
      )
    },
    enabled: false,
    // A janela é do INSTANTE do clique: guardar o resultado como fresco faria
    // "últimos 7 dias" de ontem passar por "últimos 7 dias" de hoje.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * Alertas e violações de cerca no período.
 *
 * O MAPA mostra onde o carro estava em cada ocorrência — o pino, numerado igual
 * à lista, para os dois se lerem juntos. O que continua fora é a RUA: endereço
 * é revelação à parte, no cartão de rastreamento, com a trilha dizendo quem
 * revelou. É o mesmo corte da última posição.
 *
 * A lista não some quando há mapa: leitor de tela não navega pino, e o nome da
 * cerca inteiro não cabe num marcador.
 */
function CartaoDeOcorrencias({ veiculoId }: { readonly veiculoId: string }): ReactElement | null {
  const podeLer = usePermissao('telemetry.read')
  const [dias, setDias] = useState<number>(30)
  const consulta = useOcorrencias(veiculoId, dias)
  const [selecionada, setSelecionada] = useState<string | null>(null)

  const dados = consulta.data
  // O número é a ponte entre a lista e o mapa: o mesmo "3" no marcador e no
  // item. Numerado na ordem da API (mais recente primeiro), antes de agrupar
  // por dia — senão cada dia recomeçaria do 1.
  const itens = useMemo(
    () =>
      (dados?.itens ?? []).map((item, indice) => ({
        ...item,
        numero: indice + 1,
        chave: `ocorrencia-${String(indice + 1)}`,
      })),
    [dados],
  )
  const pinos = useMemo<PinoDoMapa[]>(
    () =>
      itens.flatMap((item) =>
        item.latitude === null || item.longitude === null
          ? []
          : [
              {
                id: item.chave,
                latitude: item.latitude,
                longitude: item.longitude,
                rotulo: String(item.numero),
                titulo: `${String(item.numero)}. ${ROTULO_DA_OCORRENCIA[item.tipo]}`,
                linhas: [
                  item.quando === null
                    ? 'Sem data informada pela 3S'
                    : `${formatarData(item.quando)} às ${horaDe(item.quando)}`,
                  item.descricao,
                ],
                tom: item.chave === selecionada ? 'marca' : 'alerta',
              },
            ],
      ),
    [itens, selecionada],
  )

  if (!podeLer) {
    return null
  }

  if (consulta.isError && ApiError.is(consulta.error) && consulta.error.status === 403) {
    return null
  }

  const periodo = PERIODOS_DE_OCORRENCIA.find((opcao) => opcao.dias === dias)

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Cerca virtual e alertas</CardTitle>
        <p className="text-sm text-fg-secondary">
          Consulta ao vivo na 3S — não é espelho do nosso banco. Escolha o período e consulte; cada
          consulta fica registrada na trilha de auditoria com o seu usuário.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="w-full sm:w-auto">
            <span className="mb-1 block text-xs text-fg-muted">Período</span>
            <Select
              className="w-full sm:w-48"
              value={String(dias)}
              disabled={consulta.isFetching}
              onChange={(evento) => {
                setDias(Number(evento.target.value))
              }}
            >
              {PERIODOS_DE_OCORRENCIA.map((opcao) => (
                <option key={opcao.dias} value={opcao.dias}>
                  {opcao.rotulo}
                </option>
              ))}
            </Select>
          </label>

          <Button
            variant="secondary"
            disabled={consulta.isFetching}
            onClick={() => {
              void consulta.refetch()
            }}
          >
            {consulta.isFetching ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <ShieldAlert aria-hidden="true" />
            )}
            Consultar
          </Button>
        </div>

        {consulta.isFetching ? (
          <LoadingState rotulo="Consultando a 3S…" linhas={3} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível consultar as ocorrências"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : dados === undefined ? (
          <EmptyState
            icone={ShieldAlert}
            titulo="Nenhuma consulta feita ainda"
            descricao="Esta é a única leitura do módulo que vai à 3S na hora, e a cota é de 9 chamadas por minuto para a instalação inteira — por isso ela espera o seu clique em vez de disparar sozinha."
          />
        ) : dados.motivo === 'SEM_RASTREADOR' ? (
          <EmptyState
            icone={Satellite}
            titulo="Sem rastreador vinculado"
            descricao="Cerca e alerta nascem no cadastro da 3S. Vincule o veículo pela sincronização de frota — o cartão de rastreamento faz isso — e a consulta passa a responder."
          />
        ) : itens.length === 0 ? (
          <EmptyState
            icone={ShieldCheck}
            titulo="Nenhuma ocorrência no período"
            descricao={`Nem excesso de velocidade, nem violação de cerca ${periodo?.frase ?? 'no período'}. Vale lembrar que cerca só dispara onde há cerca cadastrada na 3S.`}
          />
        ) : (
          <>
            <Alert variant="info">
              <AlertTitle>
                <span className="tabular-money">{itens.length}</span>{' '}
                {itens.length === 1 ? 'ocorrência' : 'ocorrências'} {periodo?.frase ?? 'no período'}
              </AlertTitle>
              <AlertDescription>{resumoDasOcorrencias(itens)}.</AlertDescription>
            </Alert>

            {pinos.length === 0 ? null : (
              <Mapa
                className="h-64 sm:h-80"
                // Região, não rua: a ocorrência não tem revelação própria, e a
                // rua de onde o cliente estava é revelação da posição.
                zoomMaximo={ZOOM_SEM_ENDERECO}
                rotuloAcessivel="Mapa das ocorrências do período, numeradas como na lista abaixo"
                pinos={pinos}
                selecionadoId={selecionada}
                aoSelecionar={setSelecionada}
              />
            )}

            <div className="space-y-5">
              {porDia(itens).map((grupo) => (
                <div key={grupo.dia}>
                  <h3 className="mb-2 text-xs font-medium text-fg-muted">{grupo.dia}</h3>
                  <ul className="space-y-2">
                    {grupo.itens.map((item) => {
                      const Icone = ICONE_DA_OCORRENCIA[item.tipo]
                      const noMapa = item.latitude !== null && item.longitude !== null
                      const escolhida = item.chave === selecionada
                      return (
                        <li
                          // A 3S não manda identificador estável; o número na
                          // lista é único e só muda quando a própria lista muda.
                          key={item.chave}
                          className={
                            escolhida
                              ? 'rounded-radius border border-primary bg-elevated px-3 py-2'
                              : 'rounded-radius border border-border bg-elevated px-3 py-2'
                          }
                        >
                          {/* Contexto em cima, frase embaixo — e não tudo numa
                              linha só: em 375px o nome da cerca quebra em três
                              linhas e o nível de risco encostava no meio dela. */}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {noMapa ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelecionada(item.chave)
                                }}
                                aria-label={`Ver a ocorrência ${String(item.numero)} no mapa`}
                                aria-pressed={escolhida}
                                className={
                                  escolhida
                                    ? 'inline-flex size-6 items-center justify-center rounded-md bg-primary text-xs font-semibold tabular-nums text-primary-foreground'
                                    : 'inline-flex size-6 items-center justify-center rounded-md border border-border-strong text-xs font-semibold tabular-nums text-foreground hover:border-primary'
                                }
                              >
                                {item.numero}
                              </button>
                            ) : (
                              <span className="inline-flex size-6 items-center justify-center rounded-md border border-border text-xs tabular-nums text-fg-muted">
                                {item.numero}
                              </span>
                            )}
                            <span className="tabular-money text-sm text-fg-secondary">
                              {horaDe(item.quando)}
                            </span>
                            <Badge variant="outline">
                              <Icone aria-hidden="true" />
                              {ROTULO_DA_OCORRENCIA[item.tipo]}
                            </Badge>
                            {/* `0` é nível REAL na conta — e por isso a checagem
                                é contra `null`, nunca contra a falsidade. */}
                            {item.nivelRisco === null ? null : (
                              <span className="text-xs text-fg-muted">
                                Risco <span className="tabular-money">{item.nivelRisco}</span> na 3S
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-foreground">{item.descricao}</p>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <p className="text-xs text-fg-muted">
              O mapa mostra onde o carro estava em cada ocorrência. O endereço (a rua) continua
              mascarado e só aparece no cartão de rastreamento, com o clique registrado na trilha.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Trilha de status
// ---------------------------------------------------------------------------

function TrilhaDeStatus({ veiculoId }: { readonly veiculoId: string }): ReactElement {
  const [pagina, setPagina] = useState(1)
  const consulta = useHistoricoDeStatus(veiculoId, pagina)

  const dados = consulta.data
  const itens = dados?.itens ?? []

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Trilha de status</CardTitle>
        <p className="text-sm text-fg-secondary">
          Mais recente primeiro, com quem mudou e por quê. Nenhuma linha é corrigida ou apagada.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {consulta.isPending ? (
          <LoadingState rotulo="Carregando a trilha…" linhas={3} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar a trilha"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : itens.length === 0 ? (
          <EmptyState
            icone={History}
            titulo="Sem mudanças registradas"
            descricao="A trilha começa no cadastro do veículo e cresce a cada mudança de status."
          />
        ) : (
          <ol className="space-y-4 border-l border-border pl-5">
            {itens.map((item) => (
              <li key={item.id} className="relative">
                <span
                  aria-hidden="true"
                  className="absolute left-[-1.6rem] top-1.5 size-2 rounded-full bg-border-strong"
                />

                <div className="flex flex-wrap items-center gap-2">
                  <BadgeDeStatusDoVeiculo status={item.status} />
                  <span className="text-xs text-fg-muted">
                    {item.statusAnterior === null
                      ? 'primeiro registro do veículo'
                      : `de ${ROTULO_DO_STATUS_DE_VEICULO[item.statusAnterior].rotulo}`}
                  </span>
                </div>

                <p className="mt-1.5 text-sm text-foreground">{item.motivo}</p>

                <p className="mt-0.5 text-xs tabular-money text-fg-muted">
                  {formatarDataHora(item.criadoEm)} ·{' '}
                  {item.autor === null ? 'sistema' : item.autor.nome}
                </p>
              </li>
            ))}
          </ol>
        )}

        <Paginacao
          pagina={dados?.pagina ?? pagina}
          tamanho={dados?.tamanho ?? TAMANHO_DO_HISTORICO}
          total={dados?.total ?? 0}
          aoMudarDePagina={setPagina}
          desabilitado={consulta.isFetching}
        />
      </CardContent>
    </Card>
  )
}
